<template>
  <div
    class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0"
  >
    <div class="rounded-t bg-white mb-0 px-6 py-6">
      <h2 class="text-2xl font-bold mb-4">Signature</h2>

      <!-- Signature Method Toggle -->
      <div class="mb-6">
        <label class="mr-4 font-semibold">Select Signature Method:</label>
        <label><input type="radio" value="text" v-model="method" /> Text</label>
        <label class="ml-4"
          ><input type="radio" value="draw" v-model="method" /> Draw</label
        >
      </div>

      <!-- Typed Signature -->
      <div v-if="method === 'text'" class="mb-6">
        <label class="block font-semibold mb-2">Type your name:</label>
        <input
          v-model="typedSignature"
          class="border p-2 w-full"
          placeholder="e.g. John Smith"
        />

        <div class="mt-4">
          <label class="block font-semibold mb-1">Preview:</label>
          <div
            class="border p-4 italic text-2xl"
            :style="{ fontFamily: signatureStyle.fontFamily }"
          >
            {{ typedSignature }}
          </div>

          <select
            v-model="signatureStyle.fontFamily"
            id="fontFamily"
            class="p-2 border"
          >
            <option value="'Alex Brush', cursive">Alex Brush</option>
            <option value="'Mr Dafoe', cursive">Mr Dafoe</option>
            <option value="'MrDeHaviland', cursive">MrDeHaviland</option>
            <option value="'PWSimpleScript', cursive">PWSimpleScript</option>
          </select>
        </div>
      </div>

      <!-- Drawn Signature -->
      <div v-if="method === 'draw'" class="mb-6">
        <label class="block font-semibold mb-2">Draw your signature:</label>
        <canvas
          ref="signatureCanvas"
          width="600"
          height="300"
          class="border border-gray-400"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
        ></canvas>

        <div class="mt-2">
          <button
            @click="clearCanvas"
            class="px-3 py-1 bg-gray-300 rounded mr-2"
          >
            Clear
          </button>
        </div>
      </div>

      <!-- Submit -->
      <button
        @click="submitSignature"
        class="mt-6 px-6 py-2 bg-lightBlue-400 text-blue rounded"
      >
        Submit
      </button>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2/dist/sweetalert2.js";
export default {
  data() {
    return {
      method: "text",
      typedSignature: "",
      signatureStyle: {
        fontFamily: "'Alex Brush', cursive",
      },
      drawing: false,
      context: null,
    };
  },
  mounted() {
    this.fetchSignature();
    if (this.method === "draw") {
      this.initCanvas();
    }
  },
  watch: {
    method(newVal) {
      if (newVal === "draw") {
        this.$nextTick(() => this.initCanvas());
      }
    },
  },
  methods: {
    initCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (canvas) {
        this.context = canvas.getContext("2d");
        this.context.lineWidth = 2;
        this.context.lineCap = "round";
      }
    },
    startDrawing(e) {
      this.drawing = true;
      this.context.beginPath();
      this.context.moveTo(e.offsetX, e.offsetY);
    },
    draw(e) {
      if (!this.drawing) return;
      this.context.lineTo(e.offsetX, e.offsetY);
      this.context.stroke();
    },
    stopDrawing() {
      this.drawing = false;
    },
    clearCanvas() {
      const canvas = this.$refs.signatureCanvas;
      this.context.clearRect(0, 0, canvas.width, canvas.height);
    },
    async submitSignature() {
      try {
        Swal.fire({
          title: "Submitting...",
          didOpen: () => {
            Swal.showLoading();
          },
          allowOutsideClick: false,
        });

        let message;
        if (this.method === "text") {
          const payload = {
            signature: this.typedSignature,
            style: this.signatureStyle.fontFamily,
            type: "text",
          };
          message = await this.sendToBackend(
            `${process.env.VUE_APP_BACKEND_DOMAIN}/api/update_account_signature_client`,
            payload
          );
        } else if (this.method === "draw") {
          const canvas = this.$refs.signatureCanvas;
          const dataUrl = canvas.toDataURL("image/png");

          const blob = await (await fetch(dataUrl)).blob();
          const formData = new FormData();
          formData.append("signature", blob, "signature.png");
          formData.append("style", "canvas");
          formData.append("type", "draw");

          message = await this.sendToBackend(
            `${process.env.VUE_APP_BACKEND_DOMAIN}/api/update_account_signature_client_draw`,
            formData,
            true
          );
        }
        this.$emit("signature-updated");
        Swal.fire("Success", message, "success");
      } catch (error) {
        Swal.fire(
          "Error",
          error.message || "Failed to submit signature",
          "error"
        );
      }
    },
    async sendToBackend(url, data, isFormData = false) {
      // Removed unnecessary try/catch here to fix ESLint no-useless-catch warning
      const token = localStorage.getItem("token");
      const headers = isFormData
        ? { Authorization: `Bearer ${token}` }
        : {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          };

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: isFormData ? data : JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Unknown error");
      }

      return result.message;
    },
    async fetchSignature() {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch_account_signature_client`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const result = await response.json();
        const data = result.data;

        if (data.type === "text") {
          this.method = "text";
          this.typedSignature = data.signature;
          this.signatureStyle.fontFamily = data.style;
        } else if (data.type === "drawn") {
          this.method = "draw";
          this.$nextTick(() => {
            this.initCanvas();
            const canvas = this.$refs.signatureCanvas;
            const context = canvas.getContext("2d");
            const img = new Image();
            img.onload = () =>
              context.drawImage(img, 0, 0, canvas.width, canvas.height);
            img.src = data.signature_url;
          });
        }
      } catch (error) {
        console.error("Error fetching signature:", error);
      }
    },
  },
};
</script>
